/**
 * Search System Types
 *
 * Type definitions for the full-text search system using lunr.js
 * with IndexedDB storage and optional cloud sync.
 */

export interface SearchDocument {
  /** Unique document ID (file ID) */
  id: string;
  /** File path for display */
  filePath: string;
  /** Document title */
  title: string;
  /** Main content */
  content: string;
  /** Document type */
  type: 'document' | 'phrase';
  /** Associated tags */
  tags: string[];
  /** Custom metadata as JSON */
  metadata?: Record<string, any>;
  /** Last modification timestamp */
  lastModified: string;
  /** Last indexed timestamp */
  lastIndexed?: string;
}

export interface SearchIndexData {
  /** Index ID */
  id: string;
  /** Serialized lunr index */
  indexData: string;
  /** Index version for migrations */
  version: number;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Document count in index */
  documentCount: number;
  /** Index size in bytes */
  sizeBytes: number;
  /** Whether this is compressed */
  isCompressed: boolean;
}

export interface SearchQueueItem {
  /** Queue item ID */
  id: string;
  /** File ID to process */
  fileId: string;
  /** Operation type */
  operation: 'add' | 'update' | 'remove';
  /** Processing priority */
  priority: number;
  /** Queue timestamp */
  createdAt: string;
  /** Processing attempts */
  attempts: number;
  /** Last error if failed */
  lastError?: string;
}

export interface SearchQuery {
  /** Query string */
  query: string;
  /** Fields to search */
  fields?: string[];
  /** Type filter */
  type?: 'document' | 'phrase' | 'all';
  /** Tag filters */
  tags?: string[];
  /** Date range filter */
  dateRange?: {
    from: string;
    to: string;
  };
  /** Metadata filters */
  metadata?: Record<string, any>;
  /** Use fuzzy matching */
  fuzzy?: boolean;
  /** Wildcard search */
  wildcard?: boolean;
  /** Result limit */
  limit?: number;
  /** Result offset for pagination */
  offset?: number;
}

export interface SearchResult {
  /** Document reference */
  ref: string;
  /** Search score */
  score: number;
  /** Matched document */
  document: SearchDocument;
  /** Match metadata */
  matchData: {
    metadata: Record<string, any>;
  };
  /** Highlighted snippets */
  highlights?: {
    title?: string;
    content?: string;
    tags?: string[];
  };
}

export interface SearchResults {
  /** Query that produced these results */
  query: SearchQuery;
  /** Result items */
  results: SearchResult[];
  /** Total results count (before pagination) */
  totalCount: number;
  /** Query execution time in ms */
  executionTime: number;
  /** Whether results are from cache */
  fromCache: boolean;
}

export interface SearchSettings {
  /** Enable automatic indexing on file changes */
  autoIndex: boolean;
  /** Debounce delay for index updates in ms */
  indexDebounceMs: number;
  /** Enable index compression */
  enableCompression: boolean;
  /** Maximum index size in MB before sharding */
  maxIndexSizeMB: number;
  /** Enable cloud sync for account users */
  enableCloudSync: boolean;
  /** Sync interval in minutes */
  syncIntervalMinutes: number;
  /** Enable search history */
  enableHistory: boolean;
  /** Maximum history items */
  maxHistoryItems: number;
  /** Cache TTL in minutes */
  cacheTTLMinutes: number;
  /** Field boost configuration */
  fieldBoosts: {
    title: number;
    content: number;
    tags: number;
    type: number;
    metadata: number;
  };
}

export interface SearchSyncStatus {
  /** Whether sync is enabled */
  isEnabled: boolean;
  /** Last sync timestamp */
  lastSyncTime?: string;
  /** Pending changes count */
  pendingChanges: number;
  /** Current sync status */
  status: 'idle' | 'syncing' | 'error';
  /** Error message if failed */
  error?: string;
  /** Devices synced with */
  syncedDevices?: string[];
}

export interface SearchStats {
  /** Total indexed documents */
  totalDocuments: number;
  /** Total index size in bytes */
  totalIndexSize: number;
  /** Index version */
  indexVersion: number;
  /** Last index rebuild */
  lastRebuild?: string;
  /** Average query time in ms */
  averageQueryTime: number;
  /** Cache hit rate */
  cacheHitRate: number;
  /** Most searched terms */
  topSearchTerms: Array<{
    term: string;
    count: number;
  }>;
}

export interface SearchHistoryItem {
  /** History item ID */
  id: string;
  /** Search query */
  query: SearchQuery;
  /** Result count */
  resultCount: number;
  /** Timestamp */
  timestamp: string;
  /** Whether it was successful */
  successful: boolean;
}

export interface SearchFilter {
  /** Filter ID for persistence */
  id: string;
  /** Filter name */
  name: string;
  /** Filter configuration */
  filter: Partial<SearchQuery>;
  /** Whether this is a preset */
  isPreset: boolean;
  /** Creation timestamp */
  createdAt: string;
}

/** IndexedDB database name */
export const SEARCH_DB_NAME = 'phrasewright-search';

/** IndexedDB database version */
export const SEARCH_DB_VERSION = 1;

/** Object store names */
export const SEARCH_STORES = {
  INDEX: 'search_index',
  DOCUMENTS: 'search_documents',
  QUEUE: 'search_queue',
  HISTORY: 'search_history',
  FILTERS: 'search_filters',
  SETTINGS: 'search_settings',
} as const;

/** Default search settings */
export const DEFAULT_SEARCH_SETTINGS: SearchSettings = {
  autoIndex: true,
  indexDebounceMs: 500,
  enableCompression: true,
  maxIndexSizeMB: 50,
  enableCloudSync: false,
  syncIntervalMinutes: 15,
  enableHistory: true,
  maxHistoryItems: 100,
  cacheTTLMinutes: 5,
  fieldBoosts: {
    title: 10,
    content: 1,
    tags: 5,
    type: 3,
    metadata: 2,
  },
};
